/**
 * AuthManager — Supabase auth client wrapper.
 * Handles login, signup, logout, session refresh, and token extraction.
 * Singleton pattern matching existing codebase conventions.
 *
 * For development: falls back to a dev-mode bypass when Supabase
 * isn't configured, so the 3D app still works locally.
 */

import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js';

// Supabase config — injected via Vite env or hardcoded for dev
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

type AuthListener = (session: Session | null) => void;

class AuthManager {
  private client: SupabaseClient | null = null;
  private session: Session | null = null;
  private listeners: Set<AuthListener> = new Set();
  private _initialized = false;
  private _devMode = false;

  /**
   * Initialize the auth system.
   * If Supabase isn't configured, enters dev mode (no auth required).
   */
  async init(): Promise<void> {
    if (this._initialized) return;
    this._initialized = true;

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      console.warn('[Auth] Supabase not configured — running in dev mode (no auth)');
      this._devMode = true;
      return;
    }

    try {
      this.client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

      // Get existing session
      const { data } = await this.client.auth.getSession();
      this.session = data.session;

      // Listen for auth state changes
      this.client.auth.onAuthStateChange((_event, session) => {
        this.session = session;
        this.notifyListeners();
      });
    } catch (err) {
      console.warn('[Auth] Supabase init failed — falling back to dev mode:', err);
      this.client = null;
      this._devMode = true;
    }
  }

  // ── State ─────────────────────────────────────────────────────

  get isDevMode(): boolean {
    return this._devMode;
  }

  get isAuthenticated(): boolean {
    return this._devMode || this.session !== null;
  }

  get userId(): string {
    if (this._devMode) return 'dev-user';
    return this.session?.user?.id ?? '';
  }

  get email(): string {
    if (this._devMode) return 'dev@hatchlings.ai';
    return this.session?.user?.email ?? '';
  }

  get accessToken(): string {
    if (this._devMode) return '';
    return this.session?.access_token ?? '';
  }

  get displayName(): string {
    if (this._devMode) return 'Developer';
    const meta = this.session?.user?.user_metadata;
    return meta?.full_name || meta?.name || this.email.split('@')[0] || 'User';
  }

  // ── Auth Actions ──────────────────────────────────────────────

  async signUp(email: string, password: string): Promise<{ error?: string }> {
    if (!this.client) return { error: 'Auth not configured' };

    const { error } = await this.client.auth.signUp({ email, password });
    if (error) return { error: error.message };
    return {};
  }

  async signIn(email: string, password: string): Promise<{ error?: string }> {
    if (!this.client) return { error: 'Auth not configured' };

    const { error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return {};
  }

  async signInWithGoogle(): Promise<{ error?: string }> {
    if (!this.client) return { error: 'Auth not configured' };

    const { error } = await this.client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) return { error: error.message };
    return {};
  }

  async signOut(): Promise<void> {
    if (this.client) {
      await this.client.auth.signOut();
    }
    this.session = null;
    this.notifyListeners();
  }

  // ── Headers ───────────────────────────────────────────────────

  /**
   * Get authorization headers for API requests.
   * In dev mode, uses X-Dev-* headers for the container's dev bypass.
   */
  getAuthHeaders(): Record<string, string> {
    if (this._devMode) {
      return {
        'X-Dev-User-Id': 'dev-user',
        'X-Dev-Email': 'dev@hatchlings.ai',
      };
    }

    if (this.session?.access_token) {
      return {
        'Authorization': `Bearer ${this.session.access_token}`,
      };
    }

    return {};
  }

  // ── Listeners ─────────────────────────────────────────────────

  onAuthChange(listener: AuthListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    for (const fn of this.listeners) {
      fn(this.session);
    }
  }
}

// Singleton export
export const authManager = new AuthManager();
